/*
 * Copyright (c) 2025-present TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 */

import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

class ProductVariantPickerLinkedProducts extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    // no mouse effects in card context
    if (this.dataset.context === 'card') {
      return;
    }

    // get this
    const component = this;

    // set sizes
    this.setSizes();

    // get linked products
    let linkedProducts = [...this.querySelectorAll('.linked-product')];
    if (this.dataset.context !== 'product') {
      // re-order linked products (current product -> first slot)
      const current = linkedProducts.find((p) => p.classList.contains('current'));
      if (current) {
        const parent = current.parentElement;
        parent.prepend(current);
        linkedProducts = [...this.querySelectorAll('.linked-product')];
      }

      // mouse effects only for the first 6 products on cards and collection pages
      linkedProducts = linkedProducts.filter((el) => !el.matches(':nth-of-type(7)'));
    }

    // add mouse effects
    linkedProducts.forEach((linkedProductItem) => {
      linkedProductItem.addEventListener('mouseover', () => {
        if (this.mouseEffectsEnabled()) {
          component.variantFocus(linkedProductItem);
        }
      });
      linkedProductItem.addEventListener('mouseout', () => {
        if (this.mouseEffectsEnabled()) {
          component.variantFocusOut();
        }
      });
    });
  }

  /**
   * Check if mouse effects are enabled (should match with CSS)
   *
   * @returns {boolean}
   */
  mouseEffectsEnabled() {
    return this.dataset.context === 'product' ||
      (this.dataset.context === 'collection' && window.matchMedia('(min-width: 1280px)').matches);
  }

  /**
   * Set correct sizes attribute
   */
  setSizes() {
    const sizes = this.getPrimaryMediaElement(false)
      ?.querySelector('img')
      ?.getAttribute('sizes');
    this.querySelectorAll('.linked-product-media-image').forEach((img) => {
      img.setAttribute('sizes', sizes);
    });
  }

  /**
   * Get the primary media element
   *
   * @param {boolean} [forceActive] If true, the active (=shown) image will be used instead of always the first one
   * @returns {Element}
   */
  getPrimaryMediaElement(forceActive) {
    const productContainer = this.closest('product-card') || this.closest('.product-information');
    const mediaContainer = productContainer.querySelector(
      'slideshow-slide' + (forceActive ? '[aria-hidden="false"]' : ''
      )) || productContainer.querySelector('media-gallery li.product-media-container:first-child');
    return mediaContainer.querySelector('.product-media:first-of-type');
  }

  /**
   * Reset media item
   *
   * @param {HTMLElement} media
   */
  resetMedia(media) {
    // get original image
    const tmpContainer = this.getTemporaryImageContainer(media);
    const originalImage = tmpContainer.querySelector('img');
    if (!originalImage) {
      return;
    }

    // remove added image
    media.querySelectorAll('img').forEach((img) => {
      const parent = img.originalParentElement;
      if (parent) {
        parent.appendChild(img);
      } else {
        tmpContainer.appendChild(img);
      }
    })

    // set original image
    media.appendChild(originalImage);
  }

  /**
   * Set price
   *
   * @param {string} [price]
   */
  setPrice(price) {
    if (!price) {
      price = this.dataset.price;
    }
    if (!price) {
      return;
    }

    const productContainer = this.closest('product-card'); /* || this.closest('.product-details'); */ // currently only on collection pages
    const priceContainer = productContainer?.querySelectorAll('product-price .price');

    if (priceContainer?.length) {
      priceContainer.forEach((pc) => pc.innerText = price);
    }
  }

  /**
   * Set option label
   *
   * @param option
   */
  setOptionLabel(option) {
    const optionLabel = this.querySelector('.variant-option-caption strong');
    if (option) {
      optionLabel.innerText = option;
    } else {
      optionLabel.innerText = optionLabel.getAttribute('data-initial-option-value');
    }
  }

  /**
   * Get the temporary image container
   *
   * @param {Element} media
   */
  getTemporaryImageContainer(media) {
    // check if the container already exists
    let tmpContainer = this.querySelector('.tmp-image-container');
    if (tmpContainer) {
      return tmpContainer;
    }

    // create container
    tmpContainer = document.createElement('div');
    tmpContainer.classList.add('tmp-image-container');

    // set dimensions
    const rect = media.getBoundingClientRect();
    // set width/height
    tmpContainer.style.width = rect.width + 'px';
    tmpContainer.style.height = rect.height + 'px';

    // append
    this.appendChild(tmpContainer);
    return tmpContainer;
  }

  /**
   * Set the linked product's image
   *
   * @param {Element} [linkedProductItem]
   */
  setLinkedProductImage(linkedProductItem) {
    const media = this.getPrimaryMediaElement(true);
    if (media) {
      this.resetMedia(media);

      const tmpContainer = this.getTemporaryImageContainer(media);
      const variantImage = linkedProductItem?.querySelector('.linked-product-media-image');
      if (variantImage) {
        tmpContainer.appendChild(media.querySelector('img'));
        variantImage.originalParentElement = variantImage.parentElement;
        media.appendChild(variantImage);
      }
    }
  }

  /**
   * Focus one variant
   *
   * @param {Element} linkedProductItem
   */
  variantFocus(linkedProductItem) {
    this.setOptionLabel(linkedProductItem.getAttribute('data-option-value'));
    this.setPrice(linkedProductItem.getAttribute('data-option-price'));
    this.setLinkedProductImage(linkedProductItem);
  }

  /**
   * Loose focus on one variant
   */
  variantFocusOut() {
    this.setLinkedProductImage();
    this.setOptionLabel();
    this.setPrice();
  }
}

customElements.define('product-variant-picker-linked-products', ProductVariantPickerLinkedProducts);

class ProductVariantPickerLinkedProductsLoader extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.observeVisibility();
  }

  observeVisibility() {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          obs.unobserve(entry.target);
          this.load().then(() => {
            requestAnimationFrame(() => {
              obs.observe(this);
            });
          });
        });
      },
      {
        root: null,
        rootMargin: '1000px',
        threshold: 0.00001,
      }
    );

    observer.observe(this);
  }

  async load() {
    // already loaded or currently loading
    if (this.dataset.loaded !== 'false') {
      return;
    }

    // set loaded state
    this.dataset.loaded = 'null';

    // get product URL and append context
    const url = new URL(this.dataset.url, window.location.origin);
    if (this.dataset.context) {
      url.pathname = `${url.pathname}/${this.dataset.context}`;
    }

    // get section HTML
    sectionRenderer.getSectionHTML('section-rendering-product-linked-products', true, url)
      .then((html) => {
        // parse HTML
        const parser = new DOMParser();
        const section = parser.parseFromString(html, 'text/html');

        // workaround so no image preloading happens: hide all images first
        section.querySelectorAll('img').forEach((img) => {
          img.style.display = 'none';
        });

        // inject modified HTML
        this.innerHTML = section.body.innerHTML;

        // remove the temporary display: none
        this.querySelectorAll('img').forEach((img) => {
          img.style.display = '';
        });

        // set as loaded
        this.dataset.loaded = 'true';
      });
  }
}

customElements.define('product-variant-picker-linked-products-loader', ProductVariantPickerLinkedProductsLoader);
